/**
 * IaKlaviyoLinkBuilder — builds a promo landing link for Klaviyo campaigns.
 *
 * Output shape: {base}{path}?ia_add={variant}:{qty}(,{variant}:{qty})(&ia_discount={code})
 *   - base: the store URL.
 *   - path: where the customer should land (/, /collections/x, /products/x, …).
 *   - ia_add: the variants to pre-load — read by ia-klaviyo-cart.js on landing.
 *   - ia_discount: optional Shopify discount code(s). ia-klaviyo-cart.js applies them to the cart in the
 *     background, so the products, the code and Klaviyo's utm/_kx tracking all survive. (The old
 *     /discount/{code}?redirect= shape lost the products as soon as Klaviyo added its tracking.)
 *     A Klaviyo tag such as {% coupon_code 'Welcome' %} is kept as typed so Klaviyo fills in each person's code.
 *
 * Campaign/UTM params are added by Klaviyo. Pure client-side: nothing is sent anywhere. Param names stay in
 * sync with ia-klaviyo-cart.js. Shares the .ia-plb look from ia-promo-link-builder.css.
 */
class IaKlaviyoLinkBuilder extends HTMLElement {
  connectedCallback() {
    this.baseUrl = (this.dataset.baseUrl || '').replace(/\/+$/, '');
    this.param = this.dataset.param || 'ia_add';
    this.discountParam = this.dataset.discountParam || 'ia_discount';
    this.copyLabel = this.dataset.copyLabel || 'Copy link';
    this.copySuccessLabel = this.dataset.copySuccessLabel || 'Copied!';
    this.emptyHint = this.dataset.emptyHint || '';

    this.rows = this.querySelector('[data-rows]');
    this.rowTemplate = this.querySelector('[data-row-template]');
    this.pathInput = this.querySelector('[data-path]');
    this.discountInput = this.querySelector('[data-discount]');
    this.output = this.querySelector('[data-output]');
    this.copyButton = this.querySelector('[data-copy]');

    this.addEventListener('input', this.handleInput);
    this.addEventListener('change', this.handleInput);
    this.addEventListener('click', this.handleClick);

    this.render();
  }

  disconnectedCallback() {
    this.removeEventListener('input', this.handleInput);
    this.removeEventListener('change', this.handleInput);
    this.removeEventListener('click', this.handleClick);
  }

  handleInput = () => {
    this.render();
  };

  handleClick = (event) => {
    if (event.target.closest('[data-add]')) {
      this.addRow();
    } else if (event.target.closest('[data-remove]')) {
      this.removeRow(event.target.closest('[data-row]'));
    } else if (event.target.closest('[data-copy]')) {
      this.copyLink();
    }
  };

  addRow() {
    this.rows.appendChild(this.rowTemplate.content.cloneNode(true));
    this.rows.lastElementChild?.querySelector('[data-variant]')?.focus();
    this.render();
  }

  removeRow(row) {
    if (!row) return;
    // Always keep at least one row so the tool is never empty.
    if (this.rows.querySelectorAll('[data-row]').length <= 1) {
      row.querySelectorAll('input').forEach((input) => {
        input.value = input.dataset.qty !== undefined ? '1' : '';
      });
    } else {
      row.remove();
    }
    this.render();
  }

  // Accepts a bare ID, a variant URL, or a gid — the variant ID is the last run of digits.
  parseVariantId(value) {
    const matches = String(value).match(/\d+/g);
    return matches ? matches[matches.length - 1] : '';
  }

  collectLineItems() {
    const items = [];
    this.rows.querySelectorAll('[data-row]').forEach((row) => {
      const id = this.parseVariantId(row.querySelector('[data-variant]')?.value || '');
      if (!id) return;
      const qty = Math.max(1, parseInt(row.querySelector('[data-qty]')?.value, 10) || 1);
      items.push(`${id}:${qty}`);
    });
    return items;
  }

  buildLink() {
    const items = this.collectLineItems();
    if (!items.length) return '';

    let path = (this.pathInput?.value || '').trim() || '/';
    if (!path.startsWith('/')) path = `/${path}`;

    // The landing path carries the products in `ia_add` and the code in `ia_discount` — both read on arrival
    // by ia-klaviyo-cart.js, which adds them to the cart then tidies the URL (Klaviyo's own params stay).
    const separator = path.includes('?') ? '&' : '?';
    let landing = `${path}${separator}${this.param}=${items.join(',')}`;

    const discount = (this.discountInput?.value || '').trim();
    if (discount) {
      landing += `&${this.discountParam}=${this.encodeDiscount(discount)}`;
    }

    return `${this.baseUrl}${landing}`;
  }

  // Klaviyo template tags ({% coupon_code 'Welcome' %}, {{ … }}) stay as typed so Klaviyo can render them;
  // everything else is URL-encoded (spaces, &, #, …). Commas between several codes stay readable.
  encodeDiscount(value) {
    return value
      .split(/(\{%[\s\S]*?%\}|\{\{[\s\S]*?\}\})/)
      .map((part) => (/^\{[%{]/.test(part) ? part : encodeURIComponent(part).replace(/%2C/gi, ',')))
      .join('');
  }

  render() {
    this.link = this.buildLink();
    const hasLink = Boolean(this.link);

    this.output.textContent = hasLink ? this.link : this.emptyHint;
    this.output.classList.toggle('ia-plb__link--empty', !hasLink);

    this.copyButton.disabled = !hasLink;
    if (this.copyButton.textContent !== this.copyLabel) {
      this.copyButton.textContent = this.copyLabel;
    }
  }

  async copyLink() {
    if (!this.link) return;
    try {
      await navigator.clipboard.writeText(this.link);
    } catch (error) {
      const helper = document.createElement('textarea');
      helper.value = this.link;
      helper.setAttribute('readonly', '');
      helper.style.position = 'absolute';
      helper.style.left = '-9999px';
      this.appendChild(helper);
      helper.select();
      document.execCommand('copy');
      helper.remove();
    }

    this.copyButton.textContent = this.copySuccessLabel;
    clearTimeout(this.copyTimer);
    this.copyTimer = setTimeout(() => {
      this.copyButton.textContent = this.copyLabel;
    }, 2000);
  }
}

if (!customElements.get('ia-klaviyo-link-builder')) {
  customElements.define('ia-klaviyo-link-builder', IaKlaviyoLinkBuilder);
}
